import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import api from "@/lib/api";
import { brl, fdate } from "@/lib/format";
import NovaVendaModal from "./NovaVendaModal";
import SearchCommand from "./SearchCommand";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { NAV } from "@/lib/nav";
import { useQuery } from "@tanstack/react-query";
import {
  Dumbbell, Search, Plus, Sun, Moon, Bell, LogOut,
  Menu, X, AlertTriangle, CalendarClock,
} from "lucide-react";

const ROLE_LABELS = { admin: "Administrador", gerente: "Gerente", recepcao: "Recepção", professor: "Professor" };

function Notifications() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open) api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  }, [open]);

  const count = (data?.low_stock?.length || 0) + (data?.due_students?.length || 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
          <Bell className="h-4 w-4" />
          {data && count > 0 && (
            <span className="absolute -top-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full bg-red-500 text-[10px] font-bold text-white">
              {count > 9 ? "9+" : count}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="border-b border-border px-4 py-3 text-sm font-semibold">Notificações</div>
        <div className="max-h-80 overflow-y-auto p-2">
          {!data && <div className="p-4 text-sm text-muted-foreground">Carregando...</div>}
          {data && count === 0 && (
            <div className="p-4 text-sm text-muted-foreground">Tudo certo por aqui.</div>
          )}
          {data?.low_stock?.map((p) => (
            <div key={p.id} className="flex items-start gap-3 rounded-lg px-3 py-2 text-sm" data-testid={`notif-stock-${p.id}`}>
              <AlertTriangle className="mt-0.5 h-4 w-4 text-amber-500 shrink-0" />
              <div>
                <div className="font-medium">Estoque baixo: {p.name}</div>
                <div className="text-xs text-muted-foreground">Restam {p.stock} un (mínimo {p.min_stock})</div>
              </div>
            </div>
          ))}
          {data?.due_students?.map((s) => (
            <div key={s.id} className="flex items-start gap-3 rounded-lg px-3 py-2 text-sm" data-testid={`notif-due-${s.id}`}>
              <CalendarClock className="mt-0.5 h-4 w-4 text-sky-500 shrink-0" />
              <div>
                <div className="font-medium">
                  {s.name} {s.due_date < data.today ? "— plano vencido" : "vence em breve"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {s.plan_name} · {fdate(s.due_date)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SidebarContent({ onNavigate }) {
  const { user } = useAuth();
  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });
  const visibleNav = NAV.filter((n) => {
    if (n.adminOnly) return user.role === "admin";
    if (user.role === "admin") return true;
    const perms = settings?.permissions?.[user.role];
    return !perms || perms.includes(n.key);
  });
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-border">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-600 text-white">
          <Dumbbell className="h-4 w-4" />
        </div>
        <div className="font-display font-bold tracking-tight leading-none">
          MUSCLE<span className="text-emerald-500">FACTORY</span>
        </div>
      </div>
      <nav className="flex-1 space-y-0.5 overflow-y-auto p-3">
        {visibleNav.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            onClick={onNavigate}
            data-testid={`nav-${n.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
                isActive
                  ? "bg-accent text-foreground"
                  : "text-muted-foreground hover:bg-accent/60 hover:text-foreground"
              )
            }
          >
            <n.icon className="h-4 w-4" />
            {n.label}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-border p-4 text-xs text-muted-foreground">
        Gestão de caixa · v1.0
      </div>
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [vendaOpen, setVendaOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);

  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
      const tag = document.activeElement?.tagName;
      if (e.key.toLowerCase() === "n" && !e.metaKey && !e.ctrlKey && !e.altKey && tag !== "INPUT" && tag !== "TEXTAREA") {
        setVendaOpen(true);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-60 border-r border-border bg-card lg:block">
        <SidebarContent />
      </aside>

      {mobileNav && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileNav(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-card border-r border-border">
            <div className="flex justify-end p-2">
              <Button variant="ghost" size="icon" onClick={() => setMobileNav(false)} data-testid="close-mobile-nav">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <SidebarContent onNavigate={() => setMobileNav(false)} />
          </div>
        </div>
      )}

      <div className="lg:pl-60">
        <header className="no-print sticky top-0 z-20 flex h-16 items-center gap-2 border-b border-border bg-background/80 backdrop-blur-xl px-4 sm:px-6">
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileNav(true)} data-testid="open-mobile-nav">
            <Menu className="h-4 w-4" />
          </Button>
          <button
            data-testid="open-search-btn"
            onClick={() => setSearchOpen(true)}
            className="flex h-9 w-full max-w-xs items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm text-muted-foreground transition-colors hover:bg-accent"
          >
            <Search className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Buscar...</span>
            <kbd className="ml-auto hidden rounded border border-border px-1.5 text-[10px] sm:inline">Ctrl K</kbd>
          </button>
          <div className="flex-1" />
          <Button
            data-testid="nova-venda-btn"
            onClick={() => setVendaOpen(true)}
            className="bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
          >
            <Plus className="mr-1 h-4 w-4" /> Nova Venda
            <kbd className="ml-2 hidden rounded border border-white/30 px-1.5 text-[10px] sm:inline">N</kbd>
          </Button>
          <Notifications />
          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-btn"
                className="grid h-8 w-8 place-items-center rounded-full bg-emerald-600/15 text-xs font-bold text-emerald-500 transition-colors hover:bg-emerald-600/25"
              >
                {user.name?.slice(0, 2).toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-medium">{user.name}</div>
                <div className="text-xs font-normal text-muted-foreground">
                  {ROLE_LABELS[user.role] || user.role}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="p-4 sm:p-6 lg:p-8 animate-in fade-in duration-300">
          <Outlet />
        </main>
      </div>

      <NovaVendaModal open={vendaOpen} onOpenChange={setVendaOpen} />
      <SearchCommand open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  );
}
